import { DrillingUnitType, WellConfigurationType, WellStatusType } from '../enums/entity-enums.js';
import { AccessStatusType } from '../enums/information-enums.js';
import { TAccessRights } from './access-rights.js';
import { IPagination } from './pagination.js';

export interface IHierarchyAPIQuery extends IPagination {
  parentid?: string, // fix all FE calls
  parentId?: string,
  parenttype?: HierarchyLevelType,
}

export interface IWdDesign {
  name: string;
  subjectid: string;
  hierarchyData: {
    country: { id: string; name: string };
    field: { id: string; name: string };
    site: { id: string; name: string };
    well: { id: string; name: string };
    wellbore: { id: string; name: string };
    design: { id: string; name: string };
  };
}

export interface IMetadata {
  createdBy?: string;
  created?: Date | string;
  updated?: Date | string;
  size?: number;
  status?: AccessStatusType;
  access?: string;
  email?: string;
  hierarchyData?: {
    archive: {
      name: string;
      id: string
    };
    country?: {
      name: string;
      id: string
    };
    field?: {
      name: string;
      id: string
    };
    site?: {
      name: string;
      id: string
    };
    well?: {
      name: string;
      id: string
    };
    wellbore?: {
      name: string;
      id: string
    };
    design?: {
      name: string;
      id: string
    };
  };
}

export interface IGroup {
  name: string,
  companyid: string,
}

export interface IArchive {
  archiveid: string,
  companyid: string,
  name: string,
  location: string,
  owner: string,
  created?: string | Date,
  updated?: string | Date,
  nodeid?: string,
}

export type IArchiveToCreate = Omit<IArchive, 'archiveid' | 'created' | 'updated'>;

export interface IOrganisation {
  organisationid: string,
  name: string,
  metadata: IMetadata,
  nodeid: string,
  archiveid: string,
}

export interface ICompany {
  companyId: string,
  name: string,
  metadata: IMetadata,
  nodeid: string,
  organisationid: string,
}
export interface ICountry {
  countryid: string,
  name: string,
  metadata: IMetadata,
  nodeid?: string,
  archiveid: string,
  countrycode: string,
  companyid: string,
  settings: any,
  isedited: boolean,
}
export interface IField {
  fieldid: string,
  name: string,
  metadata: IMetadata,
  nodeid?: string,
  countryid: string,
  companyid: string,
  settings: any,
  isedited: boolean,
}
export interface ISite {
  siteid: string;
  name: string;
  metadata: IMetadata,
  nodeid?: string;
  fieldid: string;
  companyid: string,
  settings: any,
  isedited: boolean,
}
export interface IWell {
  wellid: string,
  name: string,
  metadata: IMetadata,
  nodeid?: string,
  siteid: string,
  companyid: string,
  settings: any,
  isedited: boolean,
}
export interface IWellbore {
  wellboreid: string,
  name: string,
  metadata: IMetadata,
  nodeid?: string,
  wellid: string,
  companyid: string,
  settings: any,
  isedited: boolean,
}

export interface IDesign {
  designid: string,
  name: string,
  metadata: IMetadata,
  wellboreid: string,
  last_synced_out_at?: string | null,
  deleted?: string | null,
}

export interface IDesignMetadata {
  designmetadataid: string,
  well_configuration: WellConfigurationType,
  drilling_unit: DrillingUnitType,
  water_depth: number,
  air_gap: number,
  wellhead_depth: number,
  ground_elevation: number,
  well_status: WellStatusType,
}

export enum SecurityClassification {
  OPEN = 'open',
  INTERNAL = 'internal',
  RESTRICTED = 'restricted',
  CONFIDENTIAL = 'confidential',
}

export interface INode {
  nodeid: string;
  name: string,
  role: string;
  classificationlevel: SecurityClassification,
  parentid?: string,
  parent?: {
    id: string,
    type: string,
  },
  created: Date | string;
  updated: Date | string;
  owner: string;
  locked?: boolean;
  rights?: TAccessRights;
  restricted?: boolean;
}

export type INodeToCreate = Omit<INode, 'nodeid' | 'created' | 'updated'>;

export interface IObject {
  objectid: string;
  objecttypeid: string;
  nodeid: string;
  name: string;
  data: {
    [k: string]: any;
  };
  metadata: {
    [k: string]: any;
  };
  associatedobjectid?: string,
  associateddata?: {
    [k: string]: any;
  };
  type?: string;
  sourceid: string | number;
  datalinkid: string;
  created: string | Date;
  updated: string | Date;
  synced: string | Date;
  locked?: boolean;
  classificationlevel?: SecurityClassification;
  restricted?: boolean;
  rights?: TAccessRights;
  owner: string;
  version?: number;
}

export enum HierarchyLevelType {
  Archive = 'archive',
  Country = 'country',
  Field = 'field',
  Site = 'site',
  Well = 'well',
  Wellbore = 'wellbore',
  Design = 'design',
  Node = 'node',
  Object = 'object',
}

export enum WellboreLegalDataType {
  Uwi = 'uwi',
  ApiNo = 'apiNo',
}

export interface IHierarchyEntities {
  archive: IArchive[],
  country: ICountry[],
  field: IField[],
  site: ISite[],
  well: IWell[],
  wellbore: IWellbore[],
  design: IDesign[],
}

export interface IHierarchyEntityParents {
  archive: IArchive,
  country: ICountry,
  field: IField,
  site: ISite,
  well: IWell,
  wellbore: IWellbore,
  design: IDesign,
}

export type THierarchyLevelTypeValues = `${HierarchyLevelType}`;

export type TArchivesBody = IArchive | ICountry | ISite | IField | IWell | IWellbore | INode | IObject | IDesign | IGroup | INodeToCreate;
